import { AxiosRequestConfig } from "axios";
import { useAuth } from "@/context/AuthContext";

export type Organization = {
  name: string;
  display_name: string;
  description: string;
  id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
};

export type OrganizationItem = {
  organization: Organization;
  role: string;
  joined_at: string;
  is_active: boolean;
};

export type OrganizationsResponse = {
  items: OrganizationItem[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
};

export type SwitchOrganizationResponse = {
  access_token?: string;
  refresh_token?: string;
  message?: string;
  organization?: Organization;
  user?: {
    is_super_admin?: boolean;
  };
};

export const useOrganizations = () => {
  const { axios: client, setTokens, setOrganizationId, setIsSuperAdmin } = useAuth();

  const getOrganizations = async (_jwtToken: string): Promise<OrganizationsResponse> => {
    // apiKey and jwt are now attached via interceptors; parameters kept for backward compatibility
    const url = `/auth/organizations`;
    const res = await client.get<OrganizationsResponse>(url, { params: { page: 1, per_page: 100 } });

    if (res.status !== 200 || !res.data) {
      throw new Error("Failed to fetch organizations!");
    }
    const items = [...res.data.items];
    // The picker searches its loaded options, so every API page is needed.
    // This also avoids auto-selecting a single item from an incomplete list.
    for (let page = 2; page <= res.data.pages; page++) {
      const next = await client.get<OrganizationsResponse>(url, { params: { page, per_page: 100 } });
      if (next.status !== 200 || !next.data) {
        throw new Error("Failed to fetch organizations!");
      }
      items.push(...next.data.items);
    }
    return { ...res.data, items };
  };

  const switchOrganization = async (organizationId: string, _jwtToken: string): Promise<SwitchOrganizationResponse> => {
    type SwitchPayload = SwitchOrganizationResponse;
    const url = `/auth/switch-organization/${organizationId}`;
    const res = await client.post<SwitchPayload>(url, {});

    if (res.status !== 200 || !res.data) {
      throw new Error((res.data as any)?.error || "Failed to switch organization!");
    }

    // Update tokens
    if (res.data.access_token || res.data.refresh_token) {
      setTokens((prev) => ({
        accessToken: res.data.access_token ?? prev.accessToken,
        // Preserve previous refresh token if backend doesn't rotate on org switch; rebind-after-refresh will correct context.
        refreshToken: typeof res.data.refresh_token !== "undefined" ? res.data.refresh_token : prev.refreshToken,
      }));
    }

    if (typeof res.data.user?.is_super_admin === "boolean") {
      setIsSuperAdmin(res.data.user.is_super_admin);
    }

    // Persist org selection for future refresh re-binding
    setOrganizationId(organizationId);

    return res.data;
  };

  return {
    getOrganizations,
    switchOrganization,
  };
};
